import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import queryString from 'query-string';
import classNames from 'classnames';
import FontAwesome from 'react-fontawesome';
import PropTypes from 'prop-types';
import Pagination from 'react-ultimate-pagination-bootstrap-4';

import { BillingContactType, BillingItemType, BillingItemAmountType } from 'propTypes';
import withPagination from 'hocs/withPagination';
import moment from 'moment/moment';
import Placeholder from '../Placeholder';
import { titleize, formatCurrency, formatDate } from '../../utils';
import { default as duckify, filterDuck, singleDuck } from '../../utils/duckify';
import {
  billingAccounts, billingContacts, billingItems, autopayInstallments, billingConfigs as billingConfigsDuck
} from '../../ducks';
import MonthlyStatementGenerator from './MonthlyStatementGenerator';
import SelectPicker from '../SelectPicker/SelectPicker';
import withFiltering from '../../hocs/withFiltering';
import { AutopayDateDisplay } from './AutopayDateEditor';

export const formatBillingContact = (bc) => (
  bc != null ? `${bc.contact.first_name} ${bc.contact.last_name} (${bc.school_year.name})` : <span>&nbsp;</span>
);

const BillingAmount = ({
  amount, split, adminView, ...props
}) => (
  <tr {...props}>
    <td className='billing-item-toggle' />
    <td />
    <td className='sub billing-contact'>{formatBillingContact(amount.billing_contact)}</td>
    <td className='sub billing-category'>{amount.billing_category.name}</td>
    <td className='sub billing-item-amount'>{split && formatCurrency(amount.amount)}</td>
    <td />
    {adminView && <td />}
  </tr>
);
BillingAmount.propTypes = {
  amount: BillingItemAmountType.isRequired,

  split: PropTypes.bool.isRequired,

  adminView: PropTypes.bool.isRequired
};

const BillingItem = ({
  item, onEditItem, onDeleteItem, onReverseItem, filters, billingConfig, adminView, ...props
}) => {
  const [collapsed, setCollapsed] = useState(true);

  const lastLockedDate = billingConfig && billingConfig.ledger_lock ? moment(billingConfig.ledger_lock).toDate() : null;
  const dateLocked = lastLockedDate && moment(item.billing_date).toDate() <= lastLockedDate;

  const allowEdit = !dateLocked;
  const allowDelete = !dateLocked && !item.billing_installment_id && !item.checklist_answer_id && !item.order_id;
  const allowReverse = item.type === 'charge' && !allowDelete;

  const showAmount = (amount) => {
    if (!filters) {
      return true;
    }

    if (filters.contact_id === 'None' || filters.school_year_id === 'None') {
      return !amount.billing_contact;
    }

    if (filters.contact_id && (!amount.billing_contact || amount.billing_contact.contact.id !== filters.contact_id)) {
      return false;
    }

    return !(filters.school_year_id
      && (!amount.billing_contact || amount.billing_contact.school_year.id !== filters.school_year_id));
  };

  const displayFilteredAmounts = () => {
    const filteredAmounts = [];
    item.billing_item_amounts.forEach((amount) => {
      if (showAmount(amount, filters)) {
        filteredAmounts.push(amount);
      }
    });
    return filteredAmounts.map(amount => (
      <BillingAmount
        amount={amount}
        adminView={adminView}
        split={filteredAmounts.length > 1}
        key={amount.id}
      />
    ));
  };

  return (
    <tbody {...props}>
      <tr>
        <td className='billing-item-toggle' onClick={() => setCollapsed(!collapsed)}>
          <FontAwesome name={collapsed ? 'caret-right' : 'caret-down'} />
        </td>
        <td className='billing-item-date'>{formatDate(item.billing_date)}</td>
        <td className='billing-item-description'>
          <div>
            {item.type === 'payment' && item.order_id && adminView
              ? <a href={`/setup/manage_orders?search[id]=${item.order_id}`} target='blank'>{item.description}</a>
              : item.description}
          </div>
        </td>
        <td className='billing-item-type'>
          <div>{titleize(item.type)}</div>
        </td>
        <td className={classNames('billing-item-amount', { 'negative-number': item.amount < 0 })}>
          <div>{formatCurrency(item.amount)}</div>
        </td>
        <td className={classNames('billing-item-balance', { 'negative-number': item.balance < 0 })}>
          <div>{formatCurrency(item.balance)}</div>
        </td>
        { adminView && (allowEdit || allowDelete || allowReverse) && (
          <td className='billing-item-actions'>
            <div>
              {allowEdit && (
                <button className='btn btn-xs btn-primary' title='Edit Item' onClick={() => onEditItem(item)}>
                  <FontAwesome name='pencil' />
                </button>
              )}
              {allowDelete && (
                <button className='btn btn-xs btn-danger' title='Delete Item' onClick={() => onDeleteItem(item)}>
                  <FontAwesome name='trash' />
                </button>
              )}
              {allowReverse && (
                <button className='btn btn-xs btn-primary' title='Reverse Item' onClick={() => onReverseItem(item)}>
                  <FontAwesome name='undo' />
                </button>
              )}
            </div>
          </td>
        )}
      </tr>
      { !collapsed && displayFilteredAmounts()}
    </tbody>
  );
};
BillingItem.propTypes = {
  item: BillingItemType.isRequired,

  /** Callback when Edit item is clicked */
  onEditItem: PropTypes.func.isRequired,

  /** Callback when Delete item is clicked */
  onDeleteItem: PropTypes.func.isRequired,

  /** Callback when Reverse item is clicked */
  onReverseItem: PropTypes.func.isRequired,

  filters: PropTypes.shape({
    contact_id: PropTypes.string,
    school_year_id: PropTypes.string
  }),

  /** Billing Config for customer */
  billingConfig: PropTypes.shape({
    ledger_lock: PropTypes.string
  }).isRequired,

  adminView: PropTypes.bool.isRequired
};
BillingItem.defaultProps = {
  filters: {}
};

const BillingItemList = (
  {
    account, items, onAddItem, onEditItem, onDeleteItem, onReverseItem, onEditInstallments, onSendStatement,
    billingAccountId, page, setPage, filters, setFilters, accountBillingContacts, billingConfig, adminView = true
  }
) => {
  useEffect(() => {
    setFilters(filters);
  }, []);

  const paymentIndex = items.findIndex(i => i.billing_date <= account.effective_date);
  const totalPages = Math.ceil(items.total / items.perPage);
  const billingYears = account.available_statement_years;

  const hasNoFilters = () => (!filters.contact_id && !filters.school_year_id);

  const onFilterChange = (selectorName, newValue) => {
    let newFilters = filters || {};

    if (newValue === 'None') {
      newFilters = { contact_id: 'None', school_year_id: 'None' };
    } else if (newFilters.contact_id === 'None' || newFilters.school_year_id === 'None') {
      newFilters = {};
    }

    newFilters[selectorName] = newValue;
    setFilters(newFilters);
  };

  const filterOptions = (selector) => {
    const options = [];
    const seen = {};

    accountBillingContacts.forEach((billingContact) => {
      const option = billingContact[selector];
      if (!seen[option.id]) {
        options.push({ label: option.name, value: option.id });
        seen[option.id] = 1;
      }
    });
    options.sort((a, b) => (a.label < b.label ? -1 : 1));
    return options.concat([{ label: 'All', value: '' }, { label: 'None', value: 'None' }]);
  };

  return (
    <div>
      {adminView && account.autopay_days
        && (
          <React.Fragment>
            <p><AutopayDateDisplay billingAccount={account} showExpiringDays /></p>
            <p><AutopayDateDisplay billingAccount={account} /></p>
          </React.Fragment>
        )
      }

      {adminView && (
        <div className='billing-item-list-filters'>
          <b className='inline'>Student &nbsp;</b>
          <div className='inline ledger-student-select'>
            <SelectPicker
              value={filters && filters.contact_id ? filters.contact_id : ''}
              onChange={selected => onFilterChange('contact_id', selected)}
              options={filterOptions('contact')}
              standardButton
            />
          </div>

          <b className='inline'>Term &nbsp;</b>
          <div className='inline ledger-term-select'>
            <SelectPicker
              value={filters && filters.school_year_id ? filters.school_year_id : ''}
              onChange={selected => onFilterChange('school_year_id', selected)}
              options={filterOptions('school_year')}
              standardButton
            />
          </div>

          <div className='inline'>
            <button
              type='button'
              className='contact-filters-react-clear-filters btn btn-link'
              onClick={() => setFilters({})}
              hidden={hasNoFilters()}
            >
              {'Clear Filters'}
            </button>
          </div>

          <ul className='billing-item-list-actions list-inline'>
            <li className='list-inline-item'>
              <button className='btn btn-warning' onClick={() => onAddItem('charge')}>
                <FontAwesome name='plus' /> Add Charge
              </button>
            </li>
            <li className='list-inline-item'>
              <button className='btn btn-success' onClick={() => onAddItem('credit')}>
                <FontAwesome name='money' /> Add Credit
              </button>
            </li>
            <li className='list-inline-item'>
              <button className='btn btn-success' onClick={() => onAddItem('payment')}>
                <FontAwesome name='money' /> Add Payment
              </button>
            </li>
            <li className='list-inline-item'>
              <button className='btn btn-success' onClick={() => onEditInstallments()}>
                <FontAwesome name='reorder' /> Rearrange Installments
              </button>
            </li>
            <li className='list-inline-item'>
              <MonthlyStatementGenerator billingAccountId={billingAccountId} soloButton />
            </li>
            <li className='list-inline-item'>
              <a className='btn' href={`/billing_accounts/${billingAccountId}/statement`} target='_blank'>
                <FontAwesome name='file-text-o' /> Summary Statement
              </a>
            </li>
            <li className='list-inline-item'>
              <button className='btn' onClick={() => onSendStatement()}>
                <FontAwesome name='envelope-o' /> Send Statement
              </button>
            </li>
          </ul>
        </div>
      )}

      <Placeholder.loading ready={items.ready}>

        {totalPages > 1 && (
          <div>
            <div className='contact-list-react-pagination'>
              <div className='pagination pagination-right'>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  hideFirstAndLastPageLinks
                  onChange={setPage}
                />
              </div>
            </div>
          </div>
        )}

        <table className='billing-item-list table table-borderless'>
          <thead>
            <tr>
              <th className='billing-item-toggle' />
              <th className='billing-item-date'>Date</th>
              <th>Description</th>
              <th>Type / Category</th>
              <th className='billing-item-amount'>Amount</th>
              <th className='billing-item-balance'>Balance</th>
              { adminView && <th className='billing-item-actions'>&nbsp;</th>}
            </tr>
          </thead>
          {items.map((item, i) => (
            <BillingItem
              key={i}
              item={item}
              className={classNames({ 'billing-item-effective-date': i > 0 && i === paymentIndex })}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              onReverseItem={onReverseItem}
              filters={filters}
              billingConfig={billingConfig}
              adminView={adminView}
            />
          ))}

          {items.length === 0 && (
          <tbody>
            <tr>
              <td colSpan='6'><em>No billing items found.</em></td>
            </tr>
          </tbody>
          )}

        </table>
      </Placeholder.loading>
    </div>
  );
};
BillingItemList.propTypes = {
  billingAccountId: PropTypes.string.isRequired,

  /** Line items */
  items: PropTypes.arrayOf(BillingItemType).isRequired,

  /** Callback when Add item is clicked */
  onAddItem: PropTypes.func.isRequired,

  /** Callback when Edit item is clicked */
  onEditItem: PropTypes.func.isRequired,

  /** Callback when Delete item is clicked */
  onDeleteItem: PropTypes.func.isRequired,

  /** Callback when Reverse item is clicked */
  onReverseItem: PropTypes.func.isRequired,

  /** Callback when rearrange installments is clicked */
  onEditInstallments: PropTypes.func.isRequired,

  /** Callback when send statement is clicked */
  onSendStatement: PropTypes.func.isRequired,

  account: PropTypes.shape({
    effective_date: PropTypes.string,
    autopay_days: PropTypes.array
  }).isRequired,

  /** Current page number from `withPagination` */
  page: PropTypes.number.isRequired,

  /** Callback to set page number from `withPagination` */
  setPage: PropTypes.func,

  /** Current filters from 'withFiltering' */
  filters: PropTypes.shape({
    contact_id: PropTypes.string,
    school_year_id: PropTypes.string
  }),

  /** Callback to set filters from `withFiltering` */
  setFilters: PropTypes.func,

  /** Billing Contacts loaded from association with billing account */
  accountBillingContacts: PropTypes.arrayOf(BillingContactType).isRequired,

  /** Billing Config for customer */
  billingConfig: PropTypes.shape({
    ledger_lock: PropTypes.string
  }).isRequired,

  /** Vs parent view */
  adminView: PropTypes.bool
};
BillingItemList.defaultProps = {
  setPage: () => false,
  filters: {},
  setFilters: () => false,
  adminView: true
};

const mapDucksToProps = ({
  billingAccountId, page, perPage, excludePrepayment, location, filters
}) => {
  let fullFilters = {
    ...filters,
    billing_account_id: billingAccountId,
    page: page || 1,
    per_page: perPage
  };

  if (excludePrepayment) {
    fullFilters = { ...fullFilters, exclude_prepayment: true };
  }

  return {
    account: singleDuck(billingAccounts, billingAccountId),
    items: filterDuck(billingItems, {
      ...fullFilters,
      ...queryString.parse(location.search)
    }),
    accountBillingContacts: filterDuck(billingContacts, { billing_account_id: billingAccountId }),
    billingConfig: singleDuck(billingConfigsDuck, 'current')
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  ...props,
  // Make sure we reload items when changing filters since balance/amount changes
  setFilters: (newFilters) => {
    props.setFilters(newFilters);
    dispatch(billingItems.actions.markDirty());
    dispatch(autopayInstallments.actions.markDirty());
  }
});

export default compose(
  withRouter,
  withPagination,
  withFiltering,
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps)
)(BillingItemList);
